import { useState, useEffect, useRef, useCallback } from "react";
/*
 * Telia brand palette — exact values from Telia Brand Hub "Color values":
 *   Deep Purple: #29003E (background dark)
 *   Core Purple: #990AE3 (accent / UI buttons only)
 *   Light Purple: #F4E0FF (text on dark, card bg on light)
 *   Teal: #00827C (info accent)
 */
const TELIA_PURPLE = "#990AE3";
const TELIA_DEEP = "#29003E";
const TELIA_DARK = "#3D1556";
const TELIA_LIGHT = "#F4E0FF";
const TELIA_ACCENT = "#F4E0FF";
const TELIA_MAGENTA = "#C23FE3";
const SECTIONS = ["hero", "problem", "whatIs", "uniqueness", "roadmap", "partners", "join"];
// Logo components using actual brand images
function TeliaLogo({ size = 120 }) {
  return (
    <img src={new URL("../assets/images/Telia_Logotype_RGB_Purple.png", import.meta.url)}
      alt="Telia Company Logo" style={{ height: size, objectFit: "contain" }} />
  );
}
function EricssonLogo({ size = 100 }) {
  return (
    <img src={new URL("../assets/images/Eric.png", import.meta.url)}
      alt="Ericsson Logo" style={{ height: size, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
  );
}
function EULogo({ size = 60 }) {
  const r = size * 0.38;
  const cx = size / 2;
  const cy = size / 2;
  const stars = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const sx = cx + r * Math.cos(angle);
    const sy = cy + r * Math.sin(angle);
    stars.push(
      <text key={i} x={sx} y={sy} textAnchor="middle" dominantBaseline="central"
        fontSize={size * 0.16} fill="#FFD700">★</text>
    );
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r + 4} fill="#003399" />
      {stars}
    </svg>
  );
}
// Animated purple wave background
function PurpleWaveBackground({ activeSection }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const animate = () => {
      timeRef.current += 0.008;
      const t = timeRef.current;
      const w = canvas.width;
      const h = canvas.height;
      // Deep purple gradient base
      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, "#1A0533");
      grad.addColorStop(0.5, "#2D0A4E");
      grad.addColorStop(1, "#1A0533");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      // Flowing purple waves
      const waveCount = 12;
      for (let i = 0; i < waveCount; i++) {
        ctx.beginPath();
        const baseY = h * 0.2 + (i * h * 0.06);
        const amplitude = 40 + i * 8;
        const frequency = 0.002 + i * 0.0003;
        const speed = t * (0.5 + i * 0.08);
        const sectionOffset = activeSection * 0.3;
        ctx.moveTo(-10, baseY);
        for (let x = -10; x <= w + 10; x += 4) {
          const y = baseY +
            Math.sin(x * frequency + speed + sectionOffset) * amplitude +
            Math.sin(x * frequency * 1.5 + speed * 0.7) * (amplitude * 0.3);
          ctx.lineTo(x, y);
        }
        ctx.lineTo(w + 10, h + 10);
        ctx.lineTo(-10, h + 10);
        ctx.closePath();
        const alpha = 0.03 + (i / waveCount) * 0.06;
        const purple = Math.floor(80 + i * 15);
        ctx.fillStyle = `rgba(${purple}, ${Math.floor(10 + i * 5)}, ${Math.floor(140 + i * 10)}, ${alpha})`;
        ctx.fill();
      }
      // Bright accent arc (like the slide's swirl)
      ctx.beginPath();
      const arcX = w * 0.7 + Math.sin(t * 0.3) * 50;
      const arcY = h * 0.3 + Math.cos(t * 0.2) * 30;
      const arcR = Math.min(w, h) * 0.5;
      ctx.arc(arcX, arcY, arcR, -0.8 + Math.sin(t * 0.15) * 0.2, 1.5 + Math.cos(t * 0.1) * 0.3);
      ctx.strokeStyle = `rgba(153, 10, 227, 0.15)`;
      ctx.lineWidth = 60;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(arcX, arcY, arcR * 0.85, -0.6 + Math.sin(t * 0.12) * 0.15, 1.3);
      ctx.strokeStyle = `rgba(199, 125, 255, 0.08)`;
      ctx.lineWidth = 40;
      ctx.stroke();
      // Floating particles
      for (let i = 0; i < 30; i++) {
        const px = (Math.sin(t * 0.3 + i * 47.3) * 0.5 + 0.5) * w;
        const py = (Math.cos(t * 0.2 + i * 31.7) * 0.5 + 0.5) * h;
        const pr = 1 + Math.sin(t + i) * 0.5;
        ctx.beginPath();
        ctx.arc(px, py, pr, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(199, 125, 255, ${0.2 + Math.sin(t + i * 2) * 0.15})`;
        ctx.fill();
      }
      animRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [activeSection]);
  return (
    <canvas ref={canvasRef} style={{
      position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none",
    }} />
  );
}
function Counter({ end, suffix = "", active }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) { setVal(0); return; }
    let start = 0;
    const step = end / 120;
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setVal(end); clearInterval(timer); }
      else setVal(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [active, end]);
  return <span>{val}{suffix}</span>;
}
function RoadmapTimeline({ active }) {
  const CATEGORIES = [
    { id: "orchestration", label: "Quality of Service", icon: "🔌" },
    { id: "apis", label: "Developer APIs", icon: "💻" },
    { id: "location", label: "Location-based Services", icon: "📍" },
    { id: "edge", label: "Edge Breakout & Compute", icon: "☁️" },
    { id: "latency", label: "Low Latency", icon: "⚡" },
    { id: "other", label: "Sensing & Satellite", icon: "🛰️" },
  ];
  
  const ROADMAP = {
    "2025": {
      orchestration: ["Basic slice/DNN/5QI service modelling"],
      apis: ["Quality-on-Demand API", "Positioning and tracking API"],
      location: ["Indoor dot positioning (~1-2m) incl RedCap", "GNSS RTK using 5G SA"],
      edge: ["Local breakout deployment"],
      latency: ["L4S introduction"],
      other: ["SIB9 for 5G time sync"],
    },
    "2026": {
      orchestration: ["Advanced lice/DNN/5QI service modelling continues", "URSP for Windows, iOS, Android"],
      apis: ["Geofencing CAMARA via ENL"],
      location: ["Early AI model for indoor positioning (~April)", "Outdoor positioning with TA & AoA (~10-50m)", "Passive 5G SA outdoor positioning"],
      edge: ["Compute edge node integration"],
      latency: ["L4S testing by first NorthStar partners"],
      other: ["ISAC/Sensing testing at Future by Lund"],
    },
    "2027": {
      orchestration: ["New templates for 5GAA and 5G ACIA"],
      apis: ["CAMARA Connectivity insights"],
      location: [""],
      edge: ["Rollout of +40 distributed AI Compute+Edge Breakout nodes"],
      latency: [""],
      other: ["Satellite as backhaul deployment"],
    },
    "2028": {
      orchestration: ["Proactive slice assurance"],
      apis: ["CAMARA Network slice booking"],
      location: ["High-accuracy cross-tech positioning (macro ↔ indoor ↔ RTK)"],
      edge: ["Edge breakout scaling to 100+ nodes"],
      latency: ["TSN for manufacturing and smart grids (Ethernet PDUs) – 5G LAN"],
      other: ["Sensing use-case expansion (industrial & defense)", "NR NTN, IoT NTN early deployment"],
    },
    "2029": {
      orchestration: ["Full proactive assurance"],
      apis: ["Expansion to 30+ APIs"],
      location: ["Improved positioning using ISAC"],
      edge: ["Full NorthStar integration across AI + Edge + RAN"],
      latency: ["Further uRLLC enhancements"],
      other: ["Mature ISAC ecosystem", "Full 5G/Satellite integration", "Vertical 5G coverage for aeronautics"],
    },
  };

  const YEAR_COLORS = { "2025": TELIA_LIGHT, "2026": TELIA_PURPLE, "2027": TELIA_MAGENTA, "2028": "#FFB74D", "2029": "#4DD0E1" };
  const yearKeys = Object.keys(ROADMAP);

  const [expandedYear, setExpandedYear] = useState(null);
  return (
    <div style={{ width: "100%", maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 40 }}>
        {yearKeys.map((year, i) => (
          <div key={year} onClick={() => setExpandedYear(expandedYear === i ? null : i)}
            style={{
              flex: 1, cursor: "pointer",
              transition: "all 0.5s cubic-bezier(0.16,1,0.3,1)",
              opacity: active ? 1 : 0,
              transform: active ? "translateY(0)" : `translateY(${40 + i * 15}px)`,
              transitionDelay: `${i * 0.1}s`,
            }}>
            <div style={{
              height: 8, background: YEAR_COLORS[year], borderRadius: 4, marginBottom: 14,
              boxShadow: expandedYear === i ? `0 0 24px ${YEAR_COLORS[year]}88` : `0 0 12px ${YEAR_COLORS[year]}44`,
              transition: "box-shadow 0.3s",
            }} />
            <div style={{
              fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 24, fontWeight: 700,
              color: expandedYear === i ? YEAR_COLORS[year] : "#fff", textAlign: "center", transition: "color 0.3s",
            }}>{year}</div>
          </div>
        ))}
      </div>
      {expandedYear !== null && (() => {
        const year = yearKeys[expandedYear];
        const color = YEAR_COLORS[year];
        const data = ROADMAP[year];
        return (
          <div style={{
            background: `${color}15`,
            border: `1px solid ${color}44`,
            borderRadius: 20, padding: "28px 32px",
            animation: "fadeSlideUp 0.4s ease",
          }}>
            <div style={{
              fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 13,
              color, marginBottom: 22, letterSpacing: 2, textTransform: "uppercase",
            }}>{year} Milestones</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
              {CATEGORIES.map(cat => {
                const items = data[cat.id] || [];
                if (items.length === 0) return null;
                return (
                  <div key={cat.id}>
                    <div style={{
                      fontSize: 12, fontFamily: "'Telia Sans', system-ui, sans-serif",
                      color: `${color}cc`, letterSpacing: 1, textTransform: "uppercase",
                      marginBottom: 8, display: "flex", alignItems: "center", gap: 6,
                      borderBottom: `1px solid ${color}33`, paddingBottom: 6,
                    }}>
                      <span style={{ fontSize: 14 }}>{cat.icon}</span>
                      <span style={{ fontWeight: 600 }}>{cat.label}</span>
                    </div>
                    {items.map((item, j) => (
                      <div key={j} style={{
                        display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14,
                        color: "rgba(255,255,255,0.8)", lineHeight: 1.6, marginBottom: 4,
                        animation: "fadeSlideUp 0.3s ease", animationDelay: `${j * 0.05}s`, animationFillMode: "both",
                      }}>
                        <span style={{ color, fontSize: 8, marginTop: 7, flexShrink: 0 }}>◆</span>
                        {item}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      {expandedYear === null && (
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: 14, fontStyle: "italic", marginTop: 24 }}>
          Tap a year to explore milestones
        </div>
      )}
    </div>
  );
}
function ComparisonVisual({ active }) {
  const features = [
    { name: "Network Slicing", northstar: 95, public5g: 20 },
    { name: "Indoor Positioning", northstar: 90, public5g: 10 },
    { name: "IoT (RedCap/eRedCap)", northstar: 85, public5g: 20 },
    { name: "API Exposure (CAMARA)", northstar: 80, public5g: 10 },
    { name: "Low Latency (L4S)", northstar: 80, public5g: 5 },
    { name: "V2X / Automotive", northstar: 75, public5g: 10 },
    { name: "Edge Compute & AI", northstar: 70, public5g: 15 },
    { name: "Outdoor 5G SA Positioning", northstar: 65, public5g: 5 },
    { name: "ISAC / Sensing", northstar: 10, public5g: 0 },
  ];
  return (
    <div style={{ width: "100%", maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 20, marginBottom: 20, fontSize: 12 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: `linear-gradient(135deg, ${TELIA_PURPLE}, ${TELIA_LIGHT})`, display: "inline-block" }} />
          <span style={{ color: TELIA_LIGHT }}>NorthStar</span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: "#444", display: "inline-block" }} />
          <span style={{ color: "#666" }}>Public 5G</span>
        </span>
      </div>
      {features.map((f, i) => (
        <div key={f.name} style={{
          marginBottom: 18,
          opacity: active ? 1 : 0, transform: active ? "translateX(0)" : "translateX(-30px)",
          transition: `all 0.5s ease ${i * 0.07}s`,
        }}>
          <div style={{
            fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 12, color: "rgba(255,255,255,0.7)",
            marginBottom: 6, letterSpacing: 0.5,
          }}>{f.name}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{
              position: "relative", height: 14, background: "rgba(255,255,255,0.04)", borderRadius: 7, overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: active ? `${f.northstar}%` : "0%",
                background: `linear-gradient(90deg, ${TELIA_PURPLE}, ${TELIA_LIGHT})`,
                borderRadius: 7,
                transition: `width 1.2s cubic-bezier(0.16,1,0.3,1) ${0.3 + i * 0.07}s`,
                boxShadow: `0 0 16px ${TELIA_PURPLE}55`,
              }} />
            </div>
            <div style={{
              position: "relative", height: 14, background: "rgba(255,255,255,0.04)", borderRadius: 7, overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: active ? `${f.public5g}%` : "0%",
                background: "#555", borderRadius: 7,
                transition: `width 1s ease ${0.3 + i * 0.07}s`,
              }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
export default function NorthStarPresentation() {
  const [activeSection, setActiveSection] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef(null);
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight - el.clientHeight;
    setScrollProgress(scrollTop / scrollHeight);
    const current = Math.round(scrollTop / el.clientHeight);
    if (current !== activeSection) setActiveSection(current);
  }, [activeSection]);
  const scrollTo = (idx) => {
    if (!containerRef.current) return;
    containerRef.current.scrollTo({ top: idx * containerRef.current.clientHeight, behavior: "smooth" });
  };
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);
  const sectionStyle = {
    height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center",
    alignItems: "center", position: "relative", padding: "40px 60px",
    scrollSnapAlign: "start", boxSizing: "border-box",
  };
  return (
    <div style={{
      width: "100%", height: "100vh", background: TELIA_DEEP,
      fontFamily: "'Telia Sans', system-ui, -apple-system, sans-serif",
      color: "#fff", overflow: "hidden", position: "relative",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap');
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        @keyframes glow { 0%, 100% { box-shadow: 0 0 20px ${TELIA_PURPLE}44; } 50% { box-shadow: 0 0 40px ${TELIA_PURPLE}88; } }
        * { scrollbar-width: none; } *::-webkit-scrollbar { display: none; }
      `}</style>
      <PurpleWaveBackground activeSection={activeSection} />
      {/* Nav dots */}
      <div style={{
        position: "fixed", right: 24, top: "50%", transform: "translateY(-50%)", zIndex: 100,
        display: "flex", flexDirection: "column", gap: 12,
      }}>
        {SECTIONS.map((s, i) => (
          <div key={s} onClick={() => scrollTo(i)} style={{
            width: activeSection === i ? 14 : 8, height: activeSection === i ? 14 : 8,
            borderRadius: "50%", background: activeSection === i ? TELIA_LIGHT : "rgba(255,255,255,0.2)",
            cursor: "pointer", transition: "all 0.3s",
            boxShadow: activeSection === i ? `0 0 14px ${TELIA_LIGHT}88` : "none",
          }} />
        ))}
      </div>
      {/* Progress bar */}
      <div style={{
        position: "fixed", top: 0, left: 0, height: 3, zIndex: 100,
        width: `${scrollProgress * 100}%`,
        background: `linear-gradient(90deg, ${TELIA_PURPLE}, ${TELIA_LIGHT}, ${TELIA_MAGENTA})`,
        transition: "width 0.1s",
      }} />
      {/* Logo strip top-left */}
      <div style={{
        position: "fixed", top: 16, left: 24, zIndex: 100,
        display: "flex", alignItems: "center", gap: 16, opacity: 0.7,
      }}>
        <TeliaLogo size={50} />
        <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 20 }}>×</span>
        <EricssonLogo size={50} />
      </div>
      {/* EU badge bottom-left */}
      <div style={{
        position: "fixed", bottom: 16, left: 24, zIndex: 100,
        display: "flex", alignItems: "center", gap: 10, opacity: 0.6,
      }}>
        <EULogo size={36} />
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", lineHeight: 1.3 }}>
          Co-funded by the<br />European Union
        </span>
      </div>
      <div ref={containerRef} style={{
        height: "100vh", overflowY: "auto", scrollSnapType: "y mandatory",
        position: "relative", zIndex: 1,
      }}>
        {/* ===== HERO ===== */}
        <div style={sectionStyle}>
          <div style={{ textAlign: "center", maxWidth: 900, animation: "fadeSlideUp 1s ease" }}>
            <div style={{
              fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 14, color: TELIA_LIGHT,
              letterSpacing: 5, marginBottom: 28, textTransform: "uppercase",
            }}>5G SA Innovation Network</div>
            <h1 style={{
              fontSize: "clamp(52px, 9vw, 110px)", fontWeight: 700, margin: 0, lineHeight: 0.95,
              color: "#fff", letterSpacing: -3,
            }}>NorthStar</h1>
            <p style={{ fontSize: 22, color: "rgba(255,255,255,0.55)", marginTop: 28, fontWeight: 400, lineHeight: 1.6 }}>
              Sweden's purpose-built 5G Standalone innovation environment
              <br />
              <span style={{ fontSize: 16, color: "rgba(255,255,255,0.35)" }}>
                By Telia and Ericsson — for industries shaping tomorrow
              </span>
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 40, marginTop: 52 }}>
              {[
                { n: 5, s: "+", l: "Industry Verticals" },
                { n: 99, s: ".9%", l: "5G Coverage" },
                { n: 2023, s: "", l: "Launched" },
              ].map((item, i) => (
                <div key={i} style={{
                  textAlign: "center", animation: "fadeSlideUp 1s ease",
                  animationDelay: `${0.3 + i * 0.15}s`, animationFillMode: "both",
                }}>
                  <div style={{
                    fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 34, fontWeight: 700, color: TELIA_LIGHT,
                  }}><Counter end={item.n} suffix={item.s} active={activeSection === 0} /></div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 6, letterSpacing: 1 }}>{item.l}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 64, animation: "pulse 2s infinite", color: "rgba(255,255,255,0.25)", fontSize: 13 }}>
              Scroll to explore ↓
            </div>
          </div>
        </div>
        {/* ===== THE GAP ===== */}
        <div style={sectionStyle}>
          <div style={{ maxWidth: 820, textAlign: "center" }}>
            <div style={{
              fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 13, color: TELIA_MAGENTA,
              letterSpacing: 4, marginBottom: 20, textTransform: "uppercase",
              opacity: activeSection === 1 ? 1 : 0, transition: "opacity 0.5s",
            }}>The Gap</div>
            <h2 style={{
              fontSize: "clamp(28px, 5vw, 50px)", fontWeight: 700, lineHeight: 1.2, margin: 0,
              opacity: activeSection === 1 ? 1 : 0,
              transform: activeSection === 1 ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s ease",
            }}>
              Public 5G networks can't keep pace with<br />
              <span style={{ color: TELIA_MAGENTA }}>industrial innovation</span>
            </h2>
            <p style={{
              fontSize: 17, color: "rgba(255,255,255,0.5)", marginTop: 24, lineHeight: 1.7,
              opacity: activeSection === 1 ? 1 : 0, transition: "opacity 0.6s ease 0.3s",
            }}>
              Sweden's commercial 5G excels at consumer broadband — but enterprises need
              network slicing, accurate positioning independent of GNSS, edge compute, ultra-low latency,
              and programmable APIs. Those features still aren't fully available in any Swedish public network.
            </p>
<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, marginTop: 40 }}>
  {[
    { icon: "⚡", title: "Limited or No Slicing", sub: "Granular use of QoS", desc: "Public networks aren't tailored to a variety use cases" },
    { icon: "📍", title: "Limited or No Positioning", sub: "Adoption of 5G SA positioning", desc: "Mostly cell level positioning still being used" },
    { icon: "🔒", title: "Central Traffic and Compute", sub: "Leveraging of traffic breakout", desc: "All data travels to distant clouds — no edge processing" },
  ].map((item, i) => (
    <div key={i} style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 18,
      padding: "28px 22px",
      textAlign: "center",
      opacity: activeSection === 1 ? 1 : 0,
      transform: activeSection === 1 ? "translateY(0)" : "translateY(20px)",
      transition: `all 0.5s ease ${0.4 + i * 0.1}s`,
    }}>
      <div style={{ fontSize: 30, marginBottom: 14 }}>{item.icon}</div>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{item.title}</div>
      <div style={{ fontWeight: 400, fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 12 }}>{item.sub}</div>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.55 }}>{item.desc}</div>
    </div>
  ))}
</div>

          </div>
        </div>
        {/* ===== WHAT IS NORTHSTAR ===== */}
        <div style={sectionStyle}>
          <div style={{ maxWidth: 860 }}>
            <div style={{
              fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 13, color: TELIA_LIGHT,
              letterSpacing: 4, marginBottom: 20, textTransform: "uppercase",
              opacity: activeSection === 2 ? 1 : 0, transition: "opacity 0.5s",
            }}>The Solution</div>
            <h2 style={{
              fontSize: "clamp(26px, 4.5vw, 46px)", fontWeight: 700, lineHeight: 1.2, margin: 0,
              opacity: activeSection === 2 ? 1 : 0,
              transform: activeSection === 2 ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s ease",
            }}>
              A purpose-built 5G SA network<br />
              <span style={{ color: TELIA_LIGHT }}>inside Telia's public infrastructure</span>
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 36 }}>
              {[
                { title: "Dual Core Architecture", desc: "Ericsson's 5G SA dual-mode core runs alongside Telia's commercial network — bleeding-edge features with nationwide reach.", color: TELIA_PURPLE },
                { title: "Network Slicing", desc: "Dedicated slices with custom 5QIs, DNNs, and radio resource partitions per use case — V2X, uRLLC, enterprise IoT.", color: TELIA_LIGHT },
                { title: "High-Precision Positioning", desc: "Indoor ~2m accuracy with AI-enhanced models, RTK over 5G for GNSS position correction, outdoor 5G SA Timing Advance/Uplink Angle of Arrival.", color: TELIA_MAGENTA },
                { title: "Edge Compute & AI Nodes", desc: "Local breakout, co-located compute, distributed AI inference at network edge across Sweden.", color: "#FFB74D" },
              ].map((item, i) => (
                <div key={i} style={{
                  background: `${item.color}0A`, border: `1px solid ${item.color}25`,
                  borderRadius: 18, padding: "28px 24px",
                  opacity: activeSection === 2 ? 1 : 0,
                  transform: activeSection === 2 ? "translateY(0) scale(1)" : "translateY(20px) scale(0.98)",
                  transition: `all 0.5s ease ${0.2 + i * 0.1}s`,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%", background: item.color,
                    marginBottom: 16, boxShadow: `0 0 14px ${item.color}88`,
                  }} />
                  <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 10 }}>{item.title}</div>
                  <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.65 }}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* ===== COMPARISON ===== */}
        <div style={sectionStyle}>
          <div style={{ maxWidth: 800, width: "100%" }}>
            <div style={{
              fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 13, color: "#FFB74D",
              letterSpacing: 4, marginBottom: 20, textTransform: "uppercase", textAlign: "center",
              opacity: activeSection === 3 ? 1 : 0, transition: "opacity 0.5s",
            }}>Beyond Public 5G</div>
            <h2 style={{
              fontSize: "clamp(24px, 4vw, 40px)", fontWeight: 700, textAlign: "center",
              margin: "0 0 40px 0",
              opacity: activeSection === 3 ? 1 : 0,
              transform: activeSection === 3 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.5s ease",
            }}>NorthStar vs. Sweden's Public 5G Networks</h2>
            <ComparisonVisual active={activeSection === 3} />
          </div>
        </div>
        {/* ===== ROADMAP ===== */}
        <div style={sectionStyle}>
          <div style={{ maxWidth: 950, width: "100%" }}>
            <div style={{
              fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 13, color: TELIA_PURPLE,
              letterSpacing: 4, marginBottom: 20, textTransform: "uppercase", textAlign: "center",
              opacity: activeSection === 4 ? 1 : 0, transition: "opacity 0.5s",
            }}>Roadmap 2025 – 2029</div>
            <h2 style={{
              fontSize: "clamp(24px, 4vw, 40px)", fontWeight: 700, textAlign: "center",
              margin: "0 0 36px 0",
              opacity: activeSection === 4 ? 1 : 0,
              transform: activeSection === 4 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.5s ease",
            }}>Feature-packed and <span style={{ color: TELIA_PURPLE }}>accelerating</span></h2>
            <RoadmapTimeline active={activeSection === 4} />
          </div>
        </div>
        {/* ===== PARTNERS ===== */}
        <div style={sectionStyle}>
          <div style={{ maxWidth: 820, textAlign: "center" }}>
            <div style={{
              fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 13, color: TELIA_ACCENT,
              letterSpacing: 4, marginBottom: 20, textTransform: "uppercase",
              opacity: activeSection === 5 ? 1 : 0, transition: "opacity 0.5s",
            }}>Who's In</div>
            <h2 style={{
              fontSize: "clamp(24px, 4vw, 42px)", fontWeight: 700, margin: "0 0 12px 0",
              opacity: activeSection === 5 ? 1 : 0,
              transform: activeSection === 5 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.5s ease",
            }}>From mines to battlefields</h2>
            <p style={{
              color: "rgba(255,255,255,0.45)", fontSize: 16, marginBottom: 36,
              opacity: activeSection === 5 ? 1 : 0, transition: "opacity 0.5s ease 0.2s",
            }}>NorthStar brings together Sweden's most innovative organizations including</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              {[
                { name: "AstaZero", sector: "Automotive R&D/Testing", desc: "Full-scale proving ground for autonomous driving and V2X testing" },
                { name: "Boliden", sector: "Mining", desc: "Underground 5G for autonomous vehicles in a constantly changing environment" },
                { name: "Karlstad University", sector: "Research", desc: "Advanced R&D in Low-Latency Communication and many other areas" },
                { name: "Luleå Technical University", sector: "Research", desc: "Advanced R&D in rural connectivity, AI and robotics" },
                { name: "Magna", sector: "Automotive Safety", desc: "Active vehicle safety solutions" },
                { name: "Region Stockholm", sector: "Healthcare", desc: "Indoor 5G solutions for positioning medical equipment" },
                { name: "RISE", sector: "Research", desc: "Edge AI computing for drones, agricultural robots and more" },
                { name: "Scania/Traton", sector: "Automotive", desc: "Solutions for autonomy in trucks and buses" },
                { name: "Swedish Armed Forces R&D (FOI)", sector: "Defense & NATO", desc: "Mission-critical 5G for communications" },
                { name: "Your Organization", sector: "Next?", desc: "Join the network shaping Sweden's industrial future" },
              ].map((p, i) => {
                const isYou = p.name === "Your Organization";
                return (
                <div key={i} style={{
                  background: isYou ? `${TELIA_PURPLE}18` : "rgba(255,255,255,0.03)",
                  border: `1px solid ${isYou ? `${TELIA_PURPLE}55` : "rgba(255,255,255,0.06)"}`,
                  borderRadius: 18, padding: "22px 18px", textAlign: "left",
                  opacity: activeSection === 5 ? 1 : 0,
                  transform: activeSection === 5 ? "translateY(0)" : "translateY(20px)",
                  transition: `all 0.5s ease ${0.2 + i * 0.08}s`,
                  animation: isYou && activeSection === 5 ? "glow 3s infinite" : "none",
                }}>
                  <div style={{
                    fontFamily: "'Telia Sans', system-ui, sans-serif", fontSize: 10,
                    color: isYou ? TELIA_PURPLE : "rgba(255,255,255,0.3)",
                    letterSpacing: 2, marginBottom: 8, textTransform: "uppercase",
                  }}>{p.sector}</div>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8, color: isYou ? TELIA_LIGHT : "#fff" }}>{p.name}</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>{p.desc}</div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* ===== CTA ===== */}
        <div style={sectionStyle}>
          <div style={{ maxWidth: 700, textAlign: "center" }}>
            <div style={{
              fontSize: "clamp(34px, 6vw, 60px)", fontWeight: 700, lineHeight: 1.15,
              opacity: activeSection === 6 ? 1 : 0,
              transform: activeSection === 6 ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s ease",
            }}>
              Ready to build on<br />
              <span style={{
                background: `linear-gradient(135deg, ${TELIA_PURPLE}, ${TELIA_LIGHT}, ${TELIA_MAGENTA})`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>Sweden's most advanced 5G?</span>
            </div>
            <p style={{
              fontSize: 17, color: "rgba(255,255,255,0.5)", marginTop: 24, lineHeight: 1.7,
              opacity: activeSection === 6 ? 1 : 0, transition: "opacity 0.6s ease 0.3s",
            }}>
              Whether you're in automotive, mining, manufacturing, defense, or building
              something entirely new — NorthStar gives you features no public network can.
            </p>
            <div style={{
              display: "flex", justifyContent: "center", gap: 16, marginTop: 40,
              opacity: activeSection === 6 ? 1 : 0, transition: "opacity 0.6s ease 0.5s",
            }}>
              <a href="/" style={{
                display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 34px",
                background: `linear-gradient(135deg, ${TELIA_PURPLE}, ${TELIA_LIGHT})`,
                color: "#fff", borderRadius: 14, textDecoration: "none", fontWeight: 600, fontSize: 15,
                transition: "transform 0.2s, box-shadow 0.2s", border: "none",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 30px ${TELIA_PURPLE}55`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >Explore NorthStar →</a>
              <a href="mailto:northstar@teliacompany.com?subject=NorthStar%20Inquiry" style={{
                display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 34px",
                background: "transparent", color: "rgba(255,255,255,0.6)", borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.15)", textDecoration: "none", fontWeight: 500, fontSize: 15,
                transition: "all 0.2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"; e.currentTarget.style.color = "#fff"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
              >Contact the Team</a>
            </div>
            {/* Bottom logo strip */}
            <div style={{
              display: "flex", justifyContent: "center", alignItems: "center", gap: 28,
              marginTop: 60, opacity: 0.5,
            }}>
              <TeliaLogo size={60} />
              <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 18 }}>×</span>
              <EricssonLogo size={60} />
              <span style={{ width: 1, height: 24, background: "rgba(255,255,255,0.1)" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <EULogo size={28} />
                <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", lineHeight: 1.3 }}>Co-funded by the<br />European Union</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
